import { useAuth0 } from '@auth0/auth0-react'
import { chakra, Button } from '@chakra-ui/react'
import PropTypes from 'prop-types'
import { useCallback, useMemo } from 'react'
import { GoogleIcon, MicrosoftIcon } from '../../assets/auth0_buttons/ProviderIcons'
import { REACT_APP_AUTH0_REDIRECT_URI } from '../../Constants'
import { useMFAdminAppInitializer } from '../../contexts/MFAdminAppInitializer'
import { toTitleCase } from '../../utility/Utils'

const Auth0ButtonFactory = chakra(Button, {
  baseStyle: {
    justifyContent: 'center',
    gap: 2,
    w: 'full'
  }
})

/**
 *
 * @typedef {Object} Auth0ButtonConfig
 * @property {'google' | 'microsoft'} service
 */

/**
 *
 * @param {Auth0ButtonConfig & import('@chakra-ui/react').ButtonProps} props
 * @returns {JSX.Element}
 */
export default function Auth0Button({ service = 'google', ...props }) {
  const {
    state: {
      layout: { skin }
    }
  } = useMFAdminAppInitializer()
  const { loginWithRedirect } = useAuth0()

  const Icon = useCallback(() => {
    switch (service) {
      case 'google':
        return <GoogleIcon />
      case 'microsoft':
        return <MicrosoftIcon />
      default:
        return <></>
    }
  }, [service])

  const connection = useMemo(() => {
    switch (service) {
      case 'google':
        return 'google-oauth2'
      case 'microsoft':
        return 'windowslive'
      default:
        return ''
    }
  }, [service])

  const handleAuth0Login = useCallback(async () => {
    await loginWithRedirect({
      authorizationParams: {
        connection,
        redirectUri: REACT_APP_AUTH0_REDIRECT_URI,
        prompt: 'select_account'
      }
    })
  }, [connection, loginWithRedirect])

  return (
    <Auth0ButtonFactory
      leftIcon={<Icon />}
      variant={skin === 'light' ? 'outline' : 'solid'}
      colorScheme="neutral"
      onClick={handleAuth0Login}
      {...props}
    >
      Continue with {toTitleCase(service)}
    </Auth0ButtonFactory>
  )
}

Auth0Button.propTypes = {
  service: PropTypes.oneOf(['google', 'microsoft'])
}
